import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  useTheme,
  alpha,
  Stack,
  Avatar,
  LinearProgress,
  Tabs,
  Tab,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  Collapse,
  Tooltip,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
  Divider,
  TableSortLabel,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  AccountBalance,
  Refresh,
  ExpandMore,
  ExpandLess,
  Search,
  FilterList,
  PieChart as PieChartIcon,
  BarChart as BarChartIcon,
  TableChart,
  Download,
  Category,
  AttachMoney,
  Receipt,
  CalendarMonth,
  Close,
  OpenInNew,
  Visibility,
} from '@mui/icons-material';
import { useAppSelector } from '@/hooks/useAppSelector';
import { selectCurrentCompany } from '@/store/slices/authSlice';
import { api } from '@/services/api';
import { formatCurrency } from '@/utils/formatters';
import toast from 'react-hot-toast';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
  Treemap,
} from 'recharts';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// ==================== INTERFACES ====================

interface Transacao {
  data?: string;
  dataLote?: string;
  tipo: string;
  descricao: string;
  valor: string;
  saldoApos?: string;
  mes?: string;
  ano?: number;
}

interface GrupoTransacao {
  descricao: string;
  quantidade: number;
  totalCredito: number;
  totalDebito: number;
  saldo: number;
  transacoes: Transacao[];
  expanded?: boolean;
}

interface CategoriaAgrupada {
  categoria: string;
  quantidade: number;
  total: number;
  tipo: 'credito' | 'debito';
}

const CACHE_KEY = 'financial_bi_cache';
const MAX_RETRIES = 3;
const RATE_LIMIT_WAIT_MS = 2000; // 2 seconds default wait

// Helper para verificar se é erro de rate limit
const isRateLimitError = (response: any): boolean => {
  if (!response) return false;

  const message =
    response?.error?.message ||
    response?.message ||
    response?.data?.error?.message ||
    '';

  return message.toLowerCase().includes('rate limit');
};

// Helper para extrair tempo de espera da mensagem de rate limit
const extractWaitTime = (response: any): number => {
  const message =
    response?.error?.message ||
    response?.message ||
    response?.data?.error?.message ||
    '';

  const match = message.match(/Aguarde (\d+) segundos?/i);
  if (match) {
    return parseInt(match[1], 10) * 1000 + 1000; // Add 1s buffer
  }
  return RATE_LIMIT_WAIT_MS;
};

// Helper para fetch com retry em caso de rate limit
const fetchWithRetry = async (
  fetchFn: () => Promise<any>,
  retries = MAX_RETRIES,
  context = ''
): Promise<any> => {
  try {
    const response = await fetchFn();

    // Check for rate limit in response (success: false)
    if (response?.success === false && isRateLimitError(response)) {
      if (retries > 0) {
        const waitTime = extractWaitTime(response);
        console.log(`[${context}] Rate limit hit, waiting ${waitTime}ms before retry (${retries} retries left)...`);
        await new Promise((resolve) => setTimeout(resolve, waitTime));
        return fetchWithRetry(fetchFn, retries - 1, context);
      } else {
        console.warn(`[${context}] Rate limit persisted after all retries`);
      }
    }

    return response;
  } catch (err: any) {
    // Check if error contains rate limit
    const errorData = err?.response?.data || err;
    if (isRateLimitError(errorData) && retries > 0) {
      const waitTime = extractWaitTime(errorData);
      console.log(`[${context}] Rate limit hit (exception), waiting ${waitTime}ms before retry (${retries} retries left)...`);
      await new Promise((resolve) => setTimeout(resolve, waitTime));
      return fetchWithRetry(fetchFn, retries - 1, context);
    }
    throw err;
  }
};

// Categorias baseadas em padrões reais do Sicoob
const categorizarTransacao = (descricao: string, tipo?: string): string => {
  const desc = (descricao || '').toUpperCase();
  const tipoUpper = (tipo || '').toUpperCase();

  // PIX
  if (desc.includes('PIX RECEBIDO')) return 'PIX Recebido';
  if (desc.includes('PIX EMITIDO') || desc.includes('TRANSF.REALIZADA PIX')) return 'PIX Enviado';
  if (desc.includes('DEVOLUÇÃO PIX') || desc.includes('DEVOL PIX')) return 'PIX Devolução';

  // Boletos/Cobranças
  if (desc.includes('LIQUIDAÇÃO COBRANÇA') || desc.includes('CRÉD.LIQUIDAÇÃO')) return 'Boletos Recebidos';
  if (desc.includes('TIT.COMPE EFETIVADO') || desc.includes('TÍTULO COBRANÇA') || desc.includes('PAGAMENTO DE BOLETO') || desc.includes('BOLETO INTERCREDIS')) return 'Boletos Pagos';
  if (desc.includes('TARIFA COBRANÇA')) return 'Tarifa Cobrança';

  // Transferências
  if (desc.includes('TRANSF.CONTAS') || desc.includes('CRED.TRANSF')) return 'Transferências';
  if (desc.includes('TED') || desc.includes('DOC')) return 'TED/DOC';

  // Tarifas e Taxas
  if (desc.includes('TARIFA') || desc.includes('PACOTE SERVIÇOS')) return 'Tarifas Bancárias';
  if (desc.includes('IOF')) return 'IOF';
  if (desc.includes('JUROS') || desc.includes('ENCARGOS') || desc.includes('CONTA GARANTIDA')) return 'Juros/Encargos';

  // Empréstimos (incluindo taxas e seguros relacionados)
  if (desc.includes('EMPRÉSTIMO') || desc.includes('FINANC') || desc.includes('T.A.C. EMPRÉSTIMO') || desc.includes('SEGURO EMPRÉSTIMO')) return 'Empréstimos';

  // Saques e Depósitos
  if (desc.includes('SAQUE')) return 'Saques';
  if (desc.includes('DEPÓSITO') || desc.includes('DEPOSITO')) return 'Depósitos';
  if (desc.includes('DEP.CHEQUE')) return 'Cheques';
  if (desc.includes('LIBERAÇÃO DE DEPÓSITO')) return 'Liberação Depósito';

  // Pagamentos de Convênios
  if (desc.includes('CONV.TRIBUTOS') || desc.includes('RFB') || desc.includes('DARF')) return 'Impostos Federais';
  if (desc.includes('CONV.EN.ELÉTRICA') || desc.includes('CONV.GAS') || desc.includes('GÁS') || desc.includes('ELÉTRICA E GÁS')) return 'Energia/Gás';
  if (desc.includes('CONV.SANEAMENTO') || desc.includes('AGUA')) return 'Água/Saneamento';
  if (desc.includes('CONV.TELECOMUNICAÇÕES') || desc.includes('TELEFONE')) return 'Telecomunicações';
  if (desc.includes('CONV.ORGÃOS GOV') || desc.includes('ORGÃOS GOV')) return 'Órgãos Governamentais';
  if (desc.includes('MULTAS') || desc.includes('MULTA')) return 'Multas';

  // Folha de Pagamento
  if (desc.includes('SALARIO') || desc.includes('SALÁRIO') || desc.includes('PAGAMENTO SALARIO')) return 'Folha de Pagamento';

  // Bloqueados (cheques, etc)
  if (tipoUpper === 'BLOQUEADO') return 'Valores Bloqueados';

  // Estornos
  if (desc.includes('ESTORNO') || desc.includes('DEVOLUÇÃO')) return 'Estornos';

  return 'Outros';
};

// Mapeamento de cores por categoria
const CATEGORY_COLORS: Record<string, string> = {
  'PIX Recebido': '#00C853',
  'PIX Enviado': '#FF5252',
  'PIX Devolução': '#FF9800',
  'Boletos Recebidos': '#4CAF50',
  'Boletos Pagos': '#F44336',
  'Tarifa Cobrança': '#9E9E9E',
  'Transferências': '#2196F3',
  'TED/DOC': '#3F51B5',
  'Tarifas Bancárias': '#607D8B',
  'IOF': '#795548',
  'Juros/Encargos': '#E91E63',
  'Empréstimos': '#9C27B0',
  'Saques': '#FF5722',
  'Depósitos': '#8BC34A',
  'Cheques': '#CDDC39',
  'Liberação Depósito': '#00BCD4',
  'Impostos Federais': '#673AB7',
  'Energia/Gás': '#FFC107',
  'Água/Saneamento': '#00ACC1',
  'Telecomunicações': '#5C6BC0',
  'Órgãos Governamentais': '#7E57C2',
  'Multas': '#D32F2F',
  'Folha de Pagamento': '#1976D2',
  'Valores Bloqueados': '#455A64',
  'Estornos': '#26A69A',
  'Outros': '#BDBDBD',
};

// ==================== COMPONENT ====================

const FinancialBIPage: React.FC = () => {
  const theme = useTheme();
  const company = useAppSelector(selectCurrentCompany);

  // Data states
  const [transacoes, setTransacoes] = useState<Transacao[]>([]);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);

  // Filter states
  const [filtroTipo, setFiltroTipo] = useState<'todos' | 'credito' | 'debito'>('todos');
  const [filtroCategoria, setFiltroCategoria] = useState<string>('todos');
  const [filtroBusca, setFiltroBusca] = useState('');
  const [periodoMeses, setPeriodoMeses] = useState(12);

  // View states
  const [viewMode, setViewMode] = useState<'tabela' | 'grafico'>('tabela');
  const [agruparPor, setAgruparPor] = useState<'descricao' | 'categoria' | 'mes'>('categoria');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Drill-out modal states
  const [drillOutOpen, setDrillOutOpen] = useState(false);
  const [drillOutGroup, setDrillOutGroup] = useState<GrupoTransacao | null>(null);
  const [drillOutPage, setDrillOutPage] = useState(0);
  const [drillOutRowsPerPage, setDrillOutRowsPerPage] = useState(25);
  const [drillOutSortBy, setDrillOutSortBy] = useState<'data' | 'descricao' | 'valor' | 'tipo' | 'mes'>('data');
  const [drillOutSortOrder, setDrillOutSortOrder] = useState<'asc' | 'desc'>('desc');
  const [drillOutFilterTipo, setDrillOutFilterTipo] = useState<'todos' | 'credito' | 'debito'>('todos');
  const [drillOutFilterBusca, setDrillOutFilterBusca] = useState('');
  const [drillOutGroupByMes, setDrillOutGroupByMes] = useState(false);
  const [drillOutExpandedMonths, setDrillOutExpandedMonths] = useState<Set<string>>(new Set());

  // ==================== FETCH DATA ====================

  const fetchAllTransacoes = async () => {
    setLoading(true);
    setProgress(0);

    const now = new Date();
    const allTransacoes: Transacao[] = [];
    let rateLimitHits = 0;

    for (let i = periodoMeses - 1; i >= 0; i--) {
      const date = subMonths(now, i);
      const mes = date.getMonth() + 1;
      const ano = date.getFullYear();
      const mesLabel = format(date, 'MMM/yy', { locale: ptBR });

      setProgress(Math.round(((periodoMeses - i) / periodoMeses) * 100));

      try {
        const response = await fetchWithRetry(
          () => api.get(`/conta-corrente/extrato/${mes}/${ano}`),
          MAX_RETRIES,
          `extrato/${mes}/${ano}`
        );

        if (response?.success && response.data?.transacoes) {
          response.data.transacoes.forEach((t: Transacao) => {
            allTransacoes.push({
              ...t,
              mes: mesLabel,
              ano,
            });
          });
        } else if (isRateLimitError(response)) {
          rateLimitHits++;
          console.warn(`Rate limit persisted for ${mes}/${ano} after retries`);
        }
      } catch (err) {
        console.error(`Error fetching ${mes}/${ano}:`, err);
      }

      // Delay between requests to avoid rate limiting
      await new Promise((resolve) => setTimeout(resolve, 500));
    }

    setTransacoes(allTransacoes);
    setLoading(false);

    // Save to cache
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({
        transacoes: allTransacoes,
        lastUpdate: new Date().toISOString(),
        companyId: company?.id,
        periodoMeses,
      }));
    } catch (err) {
      console.error('Error saving cache:', err);
    }

    if (rateLimitHits > 0) {
      toast(`${allTransacoes.length} transações carregadas! (${rateLimitHits} meses com rate limit)`, { icon: '⚠️' });
    } else {
      toast.success(`${allTransacoes.length} transações carregadas!`);
    }
  };

  useEffect(() => {
    // Try to load from cache first
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const data = JSON.parse(cached);
        if (data.companyId === company?.id && data.periodoMeses === periodoMeses) {
          const cacheAge = Date.now() - new Date(data.lastUpdate).getTime();
          if (cacheAge < 30 * 60 * 1000) { // 30 minutes
            setTransacoes(data.transacoes);
            setLoading(false);
            toast.success('Dados carregados do cache');
            return;
          }
        }
      }
    } catch (err) {
      console.error('Error loading cache:', err);
    }

    fetchAllTransacoes();
  }, [periodoMeses]);

  // ==================== COMPUTED DATA ====================

  // Filter transactions
  const transacoesFiltradas = useMemo(() => {
    return transacoes.filter((t) => {
      const tipo = (t.tipo || '').toUpperCase();
      const isCredito = tipo === 'C' || tipo === 'CREDITO';

      // Filter by type
      if (filtroTipo === 'credito' && !isCredito) return false;
      if (filtroTipo === 'debito' && isCredito) return false;

      // Filter by category
      if (filtroCategoria !== 'todos') {
        const categoria = categorizarTransacao(t.descricao || '', t.tipo);
        if (categoria !== filtroCategoria) return false;
      }

      // Filter by search
      if (filtroBusca) {
        const search = filtroBusca.toLowerCase();
        if (!(t.descricao || '').toLowerCase().includes(search)) return false;
      }

      return true;
    });
  }, [transacoes, filtroTipo, filtroCategoria, filtroBusca]);

  // Group transactions
  const gruposTransacoes = useMemo((): GrupoTransacao[] => {
    const grupos: Map<string, GrupoTransacao> = new Map();

    transacoesFiltradas.forEach((t) => {
      let chave: string;

      if (agruparPor === 'descricao') {
        // Normalize description (remove numbers, trim)
        chave = (t.descricao || 'Sem descrição').replace(/\d+/g, '').trim().toUpperCase();
      } else if (agruparPor === 'categoria') {
        chave = categorizarTransacao(t.descricao || '', t.tipo);
      } else {
        chave = t.mes || 'Desconhecido';
      }

      const tipo = (t.tipo || '').toUpperCase();
      const isCredito = tipo === 'C' || tipo === 'CREDITO';
      const valor = parseFloat(t.valor) || 0;

      if (!grupos.has(chave)) {
        grupos.set(chave, {
          descricao: chave,
          quantidade: 0,
          totalCredito: 0,
          totalDebito: 0,
          saldo: 0,
          transacoes: [],
        });
      }

      const grupo = grupos.get(chave)!;
      grupo.quantidade++;
      grupo.transacoes.push(t);

      if (isCredito) {
        grupo.totalCredito += valor;
      } else {
        grupo.totalDebito += valor;
      }
      grupo.saldo = grupo.totalCredito - grupo.totalDebito;
    });

    // Sort by total value (descending)
    return Array.from(grupos.values()).sort((a, b) => {
      const totalA = a.totalCredito + a.totalDebito;
      const totalB = b.totalCredito + b.totalDebito;
      return totalB - totalA;
    });
  }, [transacoesFiltradas, agruparPor]);

  // Get all categories for filter
  const categorias = useMemo(() => {
    const cats = new Set<string>();
    transacoes.forEach((t) => {
      cats.add(categorizarTransacao(t.descricao || '', t.tipo));
    });
    return Array.from(cats).sort();
  }, [transacoes]);

  // Totals
  const totals = useMemo(() => {
    let totalCredito = 0;
    let totalDebito = 0;

    transacoesFiltradas.forEach((t) => {
      const tipo = (t.tipo || '').toUpperCase();
      const isCredito = tipo === 'C' || tipo === 'CREDITO';
      const valor = parseFloat(t.valor) || 0;

      if (isCredito) {
        totalCredito += valor;
      } else {
        totalDebito += valor;
      }
    });

    return {
      credito: totalCredito,
      debito: totalDebito,
      saldo: totalCredito - totalDebito,
      quantidade: transacoesFiltradas.length,
    };
  }, [transacoesFiltradas]);

  // Chart data for Pie
  const pieDataCredito = useMemo(() => {
    return gruposTransacoes
      .filter((g) => g.totalCredito > 0)
      .map((g) => ({
        name: g.descricao,
        value: g.totalCredito,
        color: CATEGORY_COLORS[g.descricao] || CATEGORY_COLORS['Outros'],
      }))
      .slice(0, 10);
  }, [gruposTransacoes]);

  const pieDataDebito = useMemo(() => {
    return gruposTransacoes
      .filter((g) => g.totalDebito > 0)
      .map((g) => ({
        name: g.descricao,
        value: g.totalDebito,
        color: CATEGORY_COLORS[g.descricao] || CATEGORY_COLORS['Outros'],
      }))
      .slice(0, 10);
  }, [gruposTransacoes]);

  // Bar chart data
  const barChartData = useMemo(() => {
    return gruposTransacoes.slice(0, 15).map((g) => ({
      name: g.descricao.length > 20 ? g.descricao.substring(0, 20) + '...' : g.descricao,
      credito: g.totalCredito,
      debito: g.totalDebito,
    }));
  }, [gruposTransacoes]);

  // ==================== HANDLERS ====================

  const toggleGroup = (descricao: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(descricao)) {
      newExpanded.delete(descricao);
    } else {
      newExpanded.add(descricao);
    }
    setExpandedGroups(newExpanded);
  };

  const exportToCSV = () => {
    const headers = ['Grupo', 'Quantidade', 'Total Crédito', 'Total Débito', 'Saldo'];
    const rows = gruposTransacoes.map((g) => [
      g.descricao,
      g.quantidade,
      g.totalCredito.toFixed(2),
      g.totalDebito.toFixed(2),
      g.saldo.toFixed(2),
    ]);

    const csv = [headers.join(';'), ...rows.map((r) => r.join(';'))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `financeiro_bi_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
  };

  // Drill-out handlers
  const openDrillOut = (grupo: GrupoTransacao) => {
    setDrillOutGroup(grupo);
    setDrillOutPage(0);
    setDrillOutFilterTipo('todos');
    setDrillOutFilterBusca('');
    setDrillOutSortBy('data');
    setDrillOutSortOrder('desc');
    setDrillOutGroupByMes(false);
    setDrillOutExpandedMonths(new Set());
    setDrillOutOpen(true);
  };

  const closeDrillOut = () => {
    setDrillOutOpen(false);
    setDrillOutGroup(null);
  };

  const handleDrillOutSort = (column: 'data' | 'descricao' | 'valor' | 'tipo' | 'mes') => {
    if (drillOutSortBy === column) {
      setDrillOutSortOrder(drillOutSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setDrillOutSortBy(column);
      setDrillOutSortOrder('desc');
    }
    setDrillOutPage(0);
  };

  // Filtered and sorted drill-out transactions
  const drillOutTransacoes = useMemo(() => {
    if (!drillOutGroup) return [];

    let filtered = [...drillOutGroup.transacoes];

    // Filter by tipo
    if (drillOutFilterTipo !== 'todos') {
      filtered = filtered.filter((t) => {
        const tipo = (t.tipo || '').toUpperCase();
        const isCredito = tipo === 'C' || tipo === 'CREDITO';
        return drillOutFilterTipo === 'credito' ? isCredito : !isCredito;
      });
    }

    // Filter by search
    if (drillOutFilterBusca) {
      const search = drillOutFilterBusca.toLowerCase();
      filtered = filtered.filter((t) =>
        (t.descricao || '').toLowerCase().includes(search)
      );
    }

    // Sort
    filtered.sort((a, b) => {
      let comparison = 0;

      switch (drillOutSortBy) {
        case 'data':
          const dateA = new Date(a.data || a.dataLote || '1970-01-01').getTime();
          const dateB = new Date(b.data || b.dataLote || '1970-01-01').getTime();
          comparison = dateA - dateB;
          break;
        case 'descricao':
          comparison = (a.descricao || '').localeCompare(b.descricao || '');
          break;
        case 'valor':
          comparison = (parseFloat(a.valor) || 0) - (parseFloat(b.valor) || 0);
          break;
        case 'tipo':
          const tipoA = (a.tipo || '').toUpperCase();
          const tipoB = (b.tipo || '').toUpperCase();
          comparison = tipoA.localeCompare(tipoB);
          break;
        case 'mes':
          // Converter "jan/24" para data ordenável
          const mesMap: Record<string, number> = {
            'jan': 1, 'fev': 2, 'mar': 3, 'abr': 4, 'mai': 5, 'jun': 6,
            'jul': 7, 'ago': 8, 'set': 9, 'out': 10, 'nov': 11, 'dez': 12
          };
          const parseMonth = (m: string) => {
            const parts = (m || '').toLowerCase().split('/');
            if (parts.length === 2) {
              const month = mesMap[parts[0]] || 0;
              const year = parseInt(parts[1], 10) || 0;
              return year * 100 + month; // ex: 2024*100 + 3 = 202403
            }
            return 0;
          };
          comparison = parseMonth(a.mes || '') - parseMonth(b.mes || '');
          break;
      }

      return drillOutSortOrder === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [drillOutGroup, drillOutFilterTipo, drillOutFilterBusca, drillOutSortBy, drillOutSortOrder]);

  // Drill-out totals (filtered)
  const drillOutTotals = useMemo(() => {
    let totalCredito = 0;
    let totalDebito = 0;

    drillOutTransacoes.forEach((t) => {
      const tipo = (t.tipo || '').toUpperCase();
      const isCredito = tipo === 'C' || tipo === 'CREDITO';
      const valor = parseFloat(t.valor) || 0;

      if (isCredito) {
        totalCredito += valor;
      } else {
        totalDebito += valor;
      }
    });

    return {
      credito: totalCredito,
      debito: totalDebito,
      saldo: totalCredito - totalDebito,
      quantidade: drillOutTransacoes.length,
    };
  }, [drillOutTransacoes]);

  // Drill-out grouped by month
  const drillOutByMonth = useMemo(() => {
    const mesMap: Record<string, number> = {
      'jan': 1, 'fev': 2, 'mar': 3, 'abr': 4, 'mai': 5, 'jun': 6,
      'jul': 7, 'ago': 8, 'set': 9, 'out': 10, 'nov': 11, 'dez': 12
    };

    const parseMonthOrder = (m: string) => {
      const parts = (m || '').toLowerCase().split('/');
      if (parts.length === 2) {
        const month = mesMap[parts[0]] || 0;
        const year = parseInt(parts[1], 10) || 0;
        return year * 100 + month;
      }
      return 0;
    };

    const groups = new Map<string, { transacoes: Transacao[]; credito: number; debito: number }>();

    drillOutTransacoes.forEach((t) => {
      const mes = t.mes || 'Desconhecido';
      if (!groups.has(mes)) {
        groups.set(mes, { transacoes: [], credito: 0, debito: 0 });
      }
      const group = groups.get(mes)!;
      group.transacoes.push(t);

      const tipo = (t.tipo || '').toUpperCase();
      const isCredito = tipo === 'C' || tipo === 'CREDITO';
      const valor = parseFloat(t.valor) || 0;

      if (isCredito) {
        group.credito += valor;
      } else {
        group.debito += valor;
      }
    });

    // Sort by month (chronological)
    return Array.from(groups.entries())
      .sort((a, b) => parseMonthOrder(b[0]) - parseMonthOrder(a[0]))
      .map(([mes, data]) => ({
        mes,
        ...data,
        saldo: data.credito - data.debito,
      }));
  }, [drillOutTransacoes]);

  const toggleMonthExpand = (mes: string) => {
    const newExpanded = new Set(drillOutExpandedMonths);
    if (newExpanded.has(mes)) {
      newExpanded.delete(mes);
    } else {
      newExpanded.add(mes);
    }
    setDrillOutExpandedMonths(newExpanded);
  };

  const handleDrillOutPageChange = (_: unknown, newPage: number) => {
    setDrillOutPage(newPage);
  };

  const handleDrillOutRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDrillOutRowsPerPage(parseInt(event.target.value, 10));
    setDrillOutPage(0);
  };

  const exportDrillOutToCSV = () => {
    if (!drillOutGroup || drillOutTransacoes.length === 0) return;

    const headers = ['Data', 'Descrição', 'Tipo', 'Valor', 'Mês'];
    const rows = drillOutTransacoes.map((t) => {
      const tipo = (t.tipo || '').toUpperCase();
      const isCredito = tipo === 'C' || tipo === 'CREDITO';
      const valor = parseFloat(t.valor) || 0;
      const dataTransacao = t.data || t.dataLote;

      return [
        dataTransacao ? format(new Date(dataTransacao), 'dd/MM/yyyy') : '-',
        t.descricao || '-',
        isCredito ? 'Crédito' : 'Débito',
        (isCredito ? '' : '-') + valor.toFixed(2),
        t.mes || '-',
      ];
    });

    const filterSuffix = (drillOutFilterTipo !== 'todos' || drillOutFilterBusca) ? '_filtrado' : '';
    const csv = [headers.join(';'), ...rows.map((r) => r.join(';'))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `bi_${drillOutGroup.descricao.replace(/[^a-zA-Z0-9]/g, '_')}${filterSuffix}_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
  };

  // ==================== RENDER ====================

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <Paper sx={{ p: 1.5, boxShadow: theme.shadows[8] }}>
          <Typography variant="body2" fontWeight={600}>
            {payload[0].name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {formatCurrency(payload[0].value)}
          </Typography>
        </Paper>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Box>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          BI Financeiro
        </Typography>
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Carregando transações dos últimos {periodoMeses} meses...
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {progress}%
            </Typography>
          </Box>
          <LinearProgress variant="determinate" value={progress} />
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            BI Financeiro
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Análise e agrupamento de {totals.quantidade.toLocaleString()} transações
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={exportToCSV}
            size="small"
          >
            Exportar CSV
          </Button>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchAllTransacoes}
            disabled={loading}
          >
            Atualizar
          </Button>
        </Stack>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Total Entradas
                  </Typography>
                  <Typography variant="h5" fontWeight={700} color="success.main">
                    {formatCurrency(totals.credito)}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.1) }}>
                  <TrendingUp color="success" />
                </Avatar>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Total Saídas
                  </Typography>
                  <Typography variant="h5" fontWeight={700} color="error.main">
                    {formatCurrency(totals.debito)}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: alpha(theme.palette.error.main, 0.1) }}>
                  <TrendingDown color="error" />
                </Avatar>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Saldo Período
                  </Typography>
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    color={totals.saldo >= 0 ? 'success.main' : 'error.main'}
                  >
                    {formatCurrency(totals.saldo)}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                  <AccountBalance color="primary" />
                </Avatar>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Transações
                  </Typography>
                  <Typography variant="h5" fontWeight={700}>
                    {totals.quantidade.toLocaleString()}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: alpha(theme.palette.info.main, 0.1) }}>
                  <Receipt color="info" />
                </Avatar>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Período</InputLabel>
              <Select
                value={periodoMeses}
                label="Período"
                onChange={(e) => setPeriodoMeses(e.target.value as number)}
              >
                <MenuItem value={3}>Últimos 3 meses</MenuItem>
                <MenuItem value={6}>Últimos 6 meses</MenuItem>
                <MenuItem value={12}>Últimos 12 meses</MenuItem>
                <MenuItem value={24}>Últimos 24 meses</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo</InputLabel>
              <Select
                value={filtroTipo}
                label="Tipo"
                onChange={(e) => setFiltroTipo(e.target.value as 'todos' | 'credito' | 'debito')}
              >
                <MenuItem value="todos">Todos</MenuItem>
                <MenuItem value="credito">Créditos</MenuItem>
                <MenuItem value="debito">Débitos</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Categoria</InputLabel>
              <Select
                value={filtroCategoria}
                label="Categoria"
                onChange={(e) => setFiltroCategoria(e.target.value)}
              >
                <MenuItem value="todos">Todas</MenuItem>
                {categorias.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Agrupar por</InputLabel>
              <Select
                value={agruparPor}
                label="Agrupar por"
                onChange={(e) => setAgruparPor(e.target.value as 'descricao' | 'categoria' | 'mes')}
              >
                <MenuItem value="categoria">Categoria</MenuItem>
                <MenuItem value="descricao">Descrição</MenuItem>
                <MenuItem value="mes">Mês</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Buscar descrição..."
              value={filtroBusca}
              onChange={(e) => setFiltroBusca(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={1}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, v) => v && setViewMode(v)}
              size="small"
            >
              <ToggleButton value="tabela">
                <TableChart fontSize="small" />
              </ToggleButton>
              <ToggleButton value="grafico">
                <BarChartIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>
        </Grid>
      </Paper>

      {/* Content */}
      {viewMode === 'tabela' ? (
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Agrupamento por {agruparPor === 'categoria' ? 'Categoria' : agruparPor === 'descricao' ? 'Descrição' : 'Mês'}
            </Typography>
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, width: 40 }}></TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {agruparPor === 'categoria' ? 'Categoria' : agruparPor === 'descricao' ? 'Descrição' : 'Mês'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Qtd</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: 'success.main' }}>Entradas</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: 'error.main' }}>Saídas</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Saldo</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, width: 60 }}>Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {gruposTransacoes.map((grupo) => (
                    <React.Fragment key={grupo.descricao}>
                      <TableRow
                        hover
                        onClick={() => toggleGroup(grupo.descricao)}
                        sx={{ cursor: 'pointer', '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) } }}
                      >
                        <TableCell>
                          <IconButton size="small">
                            {expandedGroups.has(grupo.descricao) ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                bgcolor: CATEGORY_COLORS[grupo.descricao] || CATEGORY_COLORS['Outros'],
                                flexShrink: 0,
                              }}
                            />
                            <Typography variant="body2" fontWeight={500}>
                              {grupo.descricao}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          <Chip label={grupo.quantidade} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'success.main' }}>
                          {formatCurrency(grupo.totalCredito)}
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'error.main' }}>
                          {formatCurrency(grupo.totalDebito)}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontWeight: 600, color: grupo.saldo >= 0 ? 'success.main' : 'error.main' }}
                        >
                          {formatCurrency(grupo.saldo)}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Ver todos os registros">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                openDrillOut(grupo);
                              }}
                            >
                              <OpenInNew fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={7} sx={{ p: 0, border: 0 }}>
                          <Collapse in={expandedGroups.has(grupo.descricao)} timeout="auto" unmountOnExit>
                            <Box sx={{ bgcolor: alpha(theme.palette.grey[500], 0.04), p: 2 }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Data</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Descrição</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Tipo</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>Valor</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {grupo.transacoes.slice(0, 20).map((t, idx) => {
                                    const dataTransacao = t.data || t.dataLote;
                                    const tipo = (t.tipo || '').toUpperCase();
                                    const isCredito = tipo === 'C' || tipo === 'CREDITO';
                                    const valor = parseFloat(t.valor) || 0;

                                    return (
                                      <TableRow key={idx}>
                                        <TableCell>
                                          {dataTransacao ? format(new Date(dataTransacao), 'dd/MM/yyyy') : '-'}
                                        </TableCell>
                                        <TableCell>
                                          <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                                            {t.descricao || '-'}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>
                                          <Chip
                                            label={isCredito ? 'C' : 'D'}
                                            size="small"
                                            color={isCredito ? 'success' : 'error'}
                                          />
                                        </TableCell>
                                        <TableCell
                                          align="right"
                                          sx={{ color: isCredito ? 'success.main' : 'error.main' }}
                                        >
                                          {isCredito ? '+' : '-'}{formatCurrency(valor)}
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                  {grupo.transacoes.length > 20 && (
                                    <TableRow>
                                      <TableCell colSpan={4} align="center">
                                        <Typography variant="caption" color="text.secondary">
                                          ... e mais {grupo.transacoes.length - 20} transações
                                        </Typography>
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </TableBody>
                              </Table>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {/* Bar Chart */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Entradas vs Saídas por {agruparPor === 'categoria' ? 'Categoria' : agruparPor === 'descricao' ? 'Descrição' : 'Mês'}
                </Typography>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={barChartData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                    <YAxis dataKey="name" type="category" width={150} tick={{ fontSize: 11 }} />
                    <RechartsTooltip
                      formatter={(value: number) => formatCurrency(value)}
                      labelStyle={{ fontWeight: 600 }}
                    />
                    <Legend />
                    <Bar dataKey="credito" name="Entradas" fill={theme.palette.success.main} />
                    <Bar dataKey="debito" name="Saídas" fill={theme.palette.error.main} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Pie Charts */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom color="success.main">
                  Distribuição de Entradas
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={pieDataCredito}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      labelLine={false}
                    >
                      {pieDataCredito.map((entry, index) => (
                        <Cell key={index} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom color="error.main">
                  Distribuição de Saídas
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={pieDataDebito}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      labelLine={false}
                    >
                      {pieDataDebito.map((entry, index) => (
                        <Cell key={index} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Drill-Out Modal */}
      <Dialog
        open={drillOutOpen}
        onClose={closeDrillOut}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { maxHeight: '90vh' }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  bgcolor: drillOutGroup ? (CATEGORY_COLORS[drillOutGroup.descricao] || CATEGORY_COLORS['Outros']) : '#ccc',
                }}
              />
              <Typography variant="h6" fontWeight={600}>
                {drillOutGroup?.descricao || 'Detalhes'}
              </Typography>
              <Chip
                label={`${drillOutGroup?.quantidade || 0} registros`}
                size="small"
                color="primary"
                variant="outlined"
              />
            </Stack>
            <IconButton onClick={closeDrillOut} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 0 }}>
          {drillOutGroup && (
            <>
              {/* Filters */}
              <Box sx={{ p: 2, bgcolor: alpha(theme.palette.grey[500], 0.04), borderBottom: 1, borderColor: 'divider' }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={5}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Buscar na descrição..."
                      value={drillOutFilterBusca}
                      onChange={(e) => {
                        setDrillOutFilterBusca(e.target.value);
                        setDrillOutPage(0);
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Tipo</InputLabel>
                      <Select
                        value={drillOutFilterTipo}
                        label="Tipo"
                        onChange={(e) => {
                          setDrillOutFilterTipo(e.target.value as 'todos' | 'credito' | 'debito');
                          setDrillOutPage(0);
                        }}
                      >
                        <MenuItem value="todos">Todos</MenuItem>
                        <MenuItem value="credito">Créditos</MenuItem>
                        <MenuItem value="debito">Débitos</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                      <ToggleButtonGroup
                        value={drillOutGroupByMes ? 'grouped' : 'flat'}
                        exclusive
                        onChange={(_, v) => v && setDrillOutGroupByMes(v === 'grouped')}
                        size="small"
                      >
                        <ToggleButton value="flat">
                          <Tooltip title="Lista simples">
                            <TableChart fontSize="small" />
                          </Tooltip>
                        </ToggleButton>
                        <ToggleButton value="grouped">
                          <Tooltip title="Agrupar por mês">
                            <CalendarMonth fontSize="small" />
                          </Tooltip>
                        </ToggleButton>
                      </ToggleButtonGroup>
                      <Chip
                        label={`${drillOutTotals.quantidade} de ${drillOutGroup.quantidade}`}
                        size="small"
                        color="info"
                        variant="outlined"
                      />
                    </Stack>
                  </Grid>
                </Grid>
              </Box>

              {/* Summary Cards */}
              <Box sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Paper sx={{ p: 1.5, textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary">
                        Entradas {drillOutFilterTipo !== 'todos' || drillOutFilterBusca ? '(filtrado)' : ''}
                      </Typography>
                      <Typography variant="h6" color="success.main" fontWeight={600}>
                        {formatCurrency(drillOutTotals.credito)}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={4}>
                    <Paper sx={{ p: 1.5, textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary">
                        Saídas {drillOutFilterTipo !== 'todos' || drillOutFilterBusca ? '(filtrado)' : ''}
                      </Typography>
                      <Typography variant="h6" color="error.main" fontWeight={600}>
                        {formatCurrency(drillOutTotals.debito)}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={4}>
                    <Paper sx={{ p: 1.5, textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary">
                        Saldo {drillOutFilterTipo !== 'todos' || drillOutFilterBusca ? '(filtrado)' : ''}
                      </Typography>
                      <Typography
                        variant="h6"
                        fontWeight={600}
                        color={drillOutTotals.saldo >= 0 ? 'success.main' : 'error.main'}
                      >
                        {formatCurrency(drillOutTotals.saldo)}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>

              {/* Transactions Table or Grouped View */}
              {drillOutGroupByMes ? (
                /* Grouped by Month View */
                <TableContainer sx={{ maxHeight: 450 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, width: 40 }}></TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Mês</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Qtd</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: 'success.main' }}>Entradas</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: 'error.main' }}>Saídas</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Saldo</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {drillOutByMonth.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                            <Typography color="text.secondary">
                              Nenhum registro encontrado
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        drillOutByMonth.map((monthData) => (
                          <React.Fragment key={monthData.mes}>
                            <TableRow
                              hover
                              onClick={() => toggleMonthExpand(monthData.mes)}
                              sx={{ cursor: 'pointer', bgcolor: alpha(theme.palette.primary.main, 0.02) }}
                            >
                              <TableCell>
                                <IconButton size="small">
                                  {drillOutExpandedMonths.has(monthData.mes) ? <ExpandLess /> : <ExpandMore />}
                                </IconButton>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight={600}>
                                  {monthData.mes}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Chip label={monthData.transacoes.length} size="small" variant="outlined" />
                              </TableCell>
                              <TableCell align="right" sx={{ color: 'success.main', fontWeight: 500 }}>
                                {formatCurrency(monthData.credito)}
                              </TableCell>
                              <TableCell align="right" sx={{ color: 'error.main', fontWeight: 500 }}>
                                {formatCurrency(monthData.debito)}
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{ fontWeight: 600, color: monthData.saldo >= 0 ? 'success.main' : 'error.main' }}
                              >
                                {formatCurrency(monthData.saldo)}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell colSpan={6} sx={{ p: 0, border: 0 }}>
                                <Collapse in={drillOutExpandedMonths.has(monthData.mes)} timeout="auto" unmountOnExit>
                                  <Box sx={{ bgcolor: alpha(theme.palette.grey[500], 0.04), p: 1 }}>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell sx={{ fontWeight: 600 }}>Data</TableCell>
                                          <TableCell sx={{ fontWeight: 600 }}>Descrição</TableCell>
                                          <TableCell sx={{ fontWeight: 600 }}>Tipo</TableCell>
                                          <TableCell align="right" sx={{ fontWeight: 600 }}>Valor</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {monthData.transacoes.map((t, idx) => {
                                          const dataTransacao = t.data || t.dataLote;
                                          const tipo = (t.tipo || '').toUpperCase();
                                          const isCredito = tipo === 'C' || tipo === 'CREDITO';
                                          const valor = parseFloat(t.valor) || 0;

                                          return (
                                            <TableRow key={idx} hover>
                                              <TableCell>
                                                {dataTransacao ? format(new Date(dataTransacao), 'dd/MM/yyyy') : '-'}
                                              </TableCell>
                                              <TableCell>
                                                <Typography variant="body2" sx={{ maxWidth: 350 }}>
                                                  {t.descricao || '-'}
                                                </Typography>
                                              </TableCell>
                                              <TableCell>
                                                <Chip
                                                  label={isCredito ? 'C' : 'D'}
                                                  size="small"
                                                  color={isCredito ? 'success' : 'error'}
                                                />
                                              </TableCell>
                                              <TableCell
                                                align="right"
                                                sx={{ color: isCredito ? 'success.main' : 'error.main', fontWeight: 500 }}
                                              >
                                                {isCredito ? '+' : '-'}{formatCurrency(valor)}
                                              </TableCell>
                                            </TableRow>
                                          );
                                        })}
                                      </TableBody>
                                    </Table>
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                /* Flat List View */
                <>
                  <TableContainer sx={{ maxHeight: 400 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>
                            <TableSortLabel
                              active={drillOutSortBy === 'data'}
                              direction={drillOutSortBy === 'data' ? drillOutSortOrder : 'asc'}
                              onClick={() => handleDrillOutSort('data')}
                            >
                              Data
                            </TableSortLabel>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            <TableSortLabel
                              active={drillOutSortBy === 'descricao'}
                              direction={drillOutSortBy === 'descricao' ? drillOutSortOrder : 'asc'}
                              onClick={() => handleDrillOutSort('descricao')}
                            >
                              Descrição
                            </TableSortLabel>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            <TableSortLabel
                              active={drillOutSortBy === 'mes'}
                              direction={drillOutSortBy === 'mes' ? drillOutSortOrder : 'asc'}
                              onClick={() => handleDrillOutSort('mes')}
                            >
                              Mês
                            </TableSortLabel>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            <TableSortLabel
                              active={drillOutSortBy === 'tipo'}
                              direction={drillOutSortBy === 'tipo' ? drillOutSortOrder : 'asc'}
                              onClick={() => handleDrillOutSort('tipo')}
                            >
                              Tipo
                            </TableSortLabel>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            <TableSortLabel
                              active={drillOutSortBy === 'valor'}
                              direction={drillOutSortBy === 'valor' ? drillOutSortOrder : 'asc'}
                              onClick={() => handleDrillOutSort('valor')}
                            >
                              Valor
                            </TableSortLabel>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {drillOutTransacoes.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                              <Typography color="text.secondary">
                                Nenhum registro encontrado com os filtros aplicados
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          drillOutTransacoes
                            .slice(drillOutPage * drillOutRowsPerPage, drillOutPage * drillOutRowsPerPage + drillOutRowsPerPage)
                            .map((t, idx) => {
                              const dataTransacao = t.data || t.dataLote;
                              const tipo = (t.tipo || '').toUpperCase();
                              const isCredito = tipo === 'C' || tipo === 'CREDITO';
                              const valor = parseFloat(t.valor) || 0;

                              return (
                                <TableRow key={idx} hover>
                                  <TableCell>
                                    {dataTransacao ? format(new Date(dataTransacao), 'dd/MM/yyyy') : '-'}
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" sx={{ maxWidth: 400 }}>
                                      {t.descricao || '-'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Chip label={t.mes || '-'} size="small" variant="outlined" />
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={isCredito ? 'Crédito' : 'Débito'}
                                      size="small"
                                      color={isCredito ? 'success' : 'error'}
                                    />
                                  </TableCell>
                                  <TableCell
                                    align="right"
                                    sx={{ color: isCredito ? 'success.main' : 'error.main', fontWeight: 500 }}
                                  >
                                    {isCredito ? '+' : '-'}{formatCurrency(valor)}
                                  </TableCell>
                                </TableRow>
                              );
                            })
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Pagination - only for flat view */}
                  <TablePagination
                    component="div"
                    count={drillOutTransacoes.length}
                    page={drillOutPage}
                    onPageChange={handleDrillOutPageChange}
                    rowsPerPage={drillOutRowsPerPage}
                    onRowsPerPageChange={handleDrillOutRowsPerPageChange}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    labelRowsPerPage="Registros por página:"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                  />
                </>
              )}
            </>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={exportDrillOutToCSV}
            size="small"
          >
            Exportar CSV
          </Button>
          <Button variant="contained" onClick={closeDrillOut}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FinancialBIPage;
